import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getTier1DailySummary, getTier2DailyDowntimes } from '../../../services/downtime/DailysummaryServices';
import { mapTier1ResponseToItems, mapTier2ResponseToItems } from './mapper';
import { DailySummaryItem } from './types';

interface DowntimeDailyState {
  loading?: boolean;
  error?: string | null;
  items: DailySummaryItem[];
}

const initialState: DowntimeDailyState = {
  loading: false,
  error: null,
  items: [],
};

// Tier 1
export const fetchDailySummaryTier1 = createAsyncThunk<
  DailySummaryItem[],
  { assetId: string },
  { rejectValue: string }
>('downtimeDaily/fetchDailySummaryTier1', async ({ assetId }, thunkAPI) => {
  try {
    const response = await getTier1DailySummary(assetId);
    const items = response ? mapTier1ResponseToItems(response) : [];
    return items;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(err.message || 'Failed to fetch daily summary');
  }
});

//Tier 2
export const fetchDailySummaryTier2 = createAsyncThunk<DailySummaryItem[], { wellId: number }, { rejectValue: string }>(
  'downtimeDaily/fetchDailySummaryTier2',
  async ({ wellId }, thunkAPI) => {
    try {
      const rawResp = await getTier2DailyDowntimes(wellId);
      const rawArray: any[] = Array.isArray(rawResp) ? rawResp : (rawResp?.value ?? rawResp ?? []);
      const items: DailySummaryItem[] = mapTier2ResponseToItems(rawArray);
      return items;
    } catch (err: any) {
      return thunkAPI.rejectWithValue(err.message || 'Failed to fetch daily summary');
    }
  },
);

const downtimeDailySlice = createSlice({
  name: 'downtimeDaily',
  initialState,
  reducers: {
    clear(state) {
      state.loading = false;
      state.items = [];
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDailySummaryTier1.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDailySummaryTier1.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
        state.error = null;
      })
      .addCase(fetchDailySummaryTier1.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? 'Error fetching daily summary';
        state.items = [];
      })
      .addCase(fetchDailySummaryTier2.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDailySummaryTier2.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
        state.error = null;
      })
      .addCase(fetchDailySummaryTier2.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? 'Error fetching daily summary';
        state.items = [];
      });
  },
});

export const selectDowntimeDaily = (state: any) => state.downtimeDaily ?? initialState;

export const { clear } = downtimeDailySlice.actions;
export default downtimeDailySlice.reducer;
